import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminDashboard from './AdminDashboard';
import CourseAdmin from './CourseAdmin';
import GalleryAdmin from './GalleryAdmin';
import UserDatabase from './UserDatabase';
import ContactSubmissions from './ContactSubmissions';
import CandidateModule from './CandidateModule';
import CertificateAdmin from './CertificateAdmin';
import ExamAdmin from './ExamAdmin';
import EditExam from './EditExam';
import './AdminPanel.css';

const NAV_ITEMS = [
  { key: 'dashboard',  icon: '📊', label: 'Dashboard',  section: 'overview' },
  { key: 'courses',    icon: '📚', label: 'Courses',    countKey: 'coursesCount',    section: 'content' },
  { key: 'exams',      icon: '📝', label: 'Exams',      countKey: 'examsCount',      section: 'content' },
   { key: 'certificates', icon: '🎓', label: 'Certificates', countKey: 'certificatesCount', section: 'content' }, 
  { key: 'gallery',    icon: '🖼️', label: 'Gallery',    countKey: 'galleryCount',    section: 'content' },
  { key: 'candidates', icon: '🎓', label: 'Candidates', countKey: 'candidatesCount', section: 'people' },
  { key: 'users',      icon: '👥', label: 'Users',      countKey: 'usersCount',      section: 'people' },
  { key: 'messages',   icon: '📬', label: 'Messages',   countKey: 'messagesCount',   section: 'people' },
];

const TAB_META = {
  dashboard:  { title: 'Dashboard',         crumb: 'Overview' },
  courses:    { title: 'Course Management', crumb: 'Content' },
  exams:      { title: 'Exam Management',   crumb: 'Content' },
    certificates: { title: 'Certificate Management', crumb: 'Content' },
  gallery:    { title: 'Gallery Control',   crumb: 'Content' },
  candidates: { title: 'Candidates Portal', crumb: 'People' },
  users:      { title: 'User Database',     crumb: 'People' },
  messages:   { title: 'Contact Messages',  crumb: 'People' },
};

const API = {
  gallery:    'https://api.avtechbmt.cloud/api/v1/gallery',
  auth:       'https://api.avtechbmt.cloud/api/v1/auth',
  contact:    'https://api.avtechbmt.cloud/api/contact',
  exams:      'https://api.avtechbmt.cloud/api/exams',
  courses:    'https://api.avtechbmt.cloud/api/v1/admin/courses',
  candidates: 'https://api.avtechbmt.cloud/api/candidates',
   certificates: 'https://api.avtechbmt.cloud/api/certificates',
};

export default function AdminPanel() {
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(
    () => localStorage.getItem('adminActiveTab') || 'dashboard'
  );
  const [editingExamId, setEditingExamId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [counts, setCounts] = useState({
    galleryCount: 0,
    usersCount: 0,
    messagesCount: 0,
    candidatesCount: 0,
    examsCount: 0,
    coursesCount: 0,
    certificatesCount: 0, 
  });

  useEffect(() => {
    if (user?.role === 'admin') fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const authHeaders = useMemo(() => {
    const authToken = token || localStorage.getItem('token');
    return { Authorization: `Bearer ${authToken}` };
  }, [token]);

  const fetchStats = async () => {
    const headers = authHeaders;

    const safeFetch = async (url, extract) => {
      try {
        const res = await fetch(url, { headers });
        const data = await res.json();
        if (res.ok) extract(data);
      } catch (err) {
        console.error('Stat fetch failed:', url, err);
      }
    };

    const next = { ...counts };

   await Promise.all([
  safeFetch(API.courses,                 (d) => { if (d.success) next.coursesCount = d.data?.length || 0; }),
  safeFetch(`${API.gallery}/admin/all`,  (d) => { next.galleryCount = d.count || 0; }),
  safeFetch(API.candidates,              (d) => { if (d.success) next.candidatesCount = d.data?.length || 0; }),
  safeFetch(`${API.auth}/users`,         (d) => { next.usersCount = d.users?.length || 0; }),
  safeFetch(`${API.contact}/all`,        (d) => { next.messagesCount = d.count || 0; }),
  safeFetch(`${API.exams}/admin/all`,    (d) => { if (d.success) next.examsCount = d.count || 0; }),
  // 👇 NEW
  safeFetch(API.certificates,            (d) => { if (d.success) next.certificatesCount = d.count || 0; }),
]);

    setCounts(next);
  };

  const handleTabSelect = (tab) => {
    setActiveTab(tab);
    setEditingExamId(null);
    localStorage.setItem('adminActiveTab', tab);
    setSidebarOpen(false);
  };

  const handleEditExamClick = (examId) => setEditingExamId(examId);

  const handleCreateExamClick = () => setEditingExamId('create');

  const handleBackToExams = () => {
    setEditingExamId(null);
    fetchStats();
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="admin-panel">
        <div className="admin-unauthorized">
          <div className="card">
            <span className="field-label" style={{ color: 'var(--danger)' }}>
              00 / Unauthorized
            </span>
            <h2>Access Restricted</h2>
            <p>Administrative privileges are required to view this panel.</p>
            <button className="btn-primary" onClick={() => navigate('/profile')}>
              ← Return to Profile
            </button>
          </div>
        </div>
      </div>
    );
  }

  const meta = TAB_META[activeTab] || TAB_META.dashboard;
  const initials = (user.name || user.email || 'A')
    .split(' ')
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const groupedNav = {
    overview: NAV_ITEMS.filter((i) => i.section === 'overview'),
    content:  NAV_ITEMS.filter((i) => i.section === 'content'),
    people:   NAV_ITEMS.filter((i) => i.section === 'people'),
  };

  return (
    <div className="admin-panel">
      <div className="admin-layout">
        {/* ==================== VERTICAL SIDEBAR ==================== */}
        <aside className={`admin-sidebar ${sidebarOpen ? 'mobile-show' : ''}`}>
          <div className="sidebar-header">
            <span className="field-label">04 / Control Center</span>
            <h1 className="sidebar-brand">Admin Terminal</h1>
          </div>

          <nav className="sidebar-nav">
            <span className="sidebar-section-label">Overview</span>
            {groupedNav.overview.map((item) => (
              <NavButton
                key={item.key}
                item={item}
                active={activeTab === item.key}
                count={item.countKey ? counts[item.countKey] : null}
                onClick={() => handleTabSelect(item.key)}
              />
            ))}

            <span className="sidebar-section-label">Content</span>
            {groupedNav.content.map((item) => (
              <NavButton
                key={item.key}
                item={item}
                active={activeTab === item.key}
                count={item.countKey ? counts[item.countKey] : null}
                onClick={() => handleTabSelect(item.key)}
              />
            ))}

            <span className="sidebar-section-label">People</span>
            {groupedNav.people.map((item) => (
              <NavButton
                key={item.key}
                item={item}
                active={activeTab === item.key}
                count={item.countKey ? counts[item.countKey] : null}
                onClick={() => handleTabSelect(item.key)}
              />
            ))}
          </nav>

          <div className="sidebar-footer">
            <div className="sidebar-user">
              <div className="avatar">{initials}</div>
              <div className="user-info">
                <div className="user-name">{user.name || 'Administrator'}</div>
                <div className="user-role">{user.role}</div>
              </div>
            </div>
            <button className="exit-btn" onClick={() => navigate('/profile')}>
              ← Exit Terminal
            </button>
          </div>
        </aside>

        {sidebarOpen && (
          <div
            className="sidebar-backdrop"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* ==================== HORIZONTAL TOP BAR ==================== */}
        <header className="admin-topbar">
          <div className="topbar-left">
            <button
              className="mobile-menu-btn mobile-only"
              onClick={() => setSidebarOpen((s) => !s)}
              aria-label="Toggle navigation"
            >
              <span />
              <span />
              <span />
            </button>

            <div style={{ minWidth: 0 }}>
              <div className="topbar-breadcrumb">
                <span className="crumb-root">Admin</span>
                <span className="crumb-sep">/</span>
                <span>{meta.crumb}</span>
                <span className="crumb-sep">/</span>
                <span className="crumb-current">{meta.title}</span>
              </div>
              <h2 className="topbar-title">{meta.title}</h2>
            </div>
          </div>

          <div className="topbar-right">
            <span className="topbar-status">
              <span className="dot" />
              System Online
            </span>
            <button className="topbar-action" onClick={fetchStats}>
              ↻ <span>Refresh</span>
            </button>
            <button
              className="topbar-user"
              onClick={() => navigate('/profile')}
            >
              <span className="avatar">{initials}</span>
              <span className="name">{user.name || 'Admin'}</span>
            </button>
          </div>
        </header>

        {/* ==================== MAIN CONTENT ==================== */}
        <main className="admin-main" key={activeTab}>
          {activeTab === 'dashboard' && (
            <AdminDashboard
              usersCount={counts.usersCount}
              galleryCount={counts.galleryCount}
              messagesCount={counts.messagesCount}
              candidatesCount={counts.candidatesCount}
              examsCount={counts.examsCount}
              coursesCount={counts.coursesCount}
              onSelectTab={handleTabSelect}
            />
          )}
{activeTab === 'certificates' && <CertificateAdmin />}
          {activeTab === 'courses' && <CourseAdmin />}

          {activeTab === 'exams' && (
            editingExamId ? (
              <EditExam
                examId={editingExamId === 'create' ? null : editingExamId}
                onBack={handleBackToExams}
              />
            ) : (
              <ExamAdmin
                onEditExam={handleEditExamClick}
                onCreateExam={handleCreateExamClick}
              />
            )
          )}

          {activeTab === 'gallery' && <GalleryAdmin />}
          {activeTab === 'users' && <UserDatabase />}
          {activeTab === 'messages' && <ContactSubmissions />}
          {activeTab === 'candidates' && <CandidateModule />}
        </main>
      </div>
    </div>
  );
}

function NavButton({ item, active, count, onClick }) {
  return (
    <button
      className={`nav-item ${active ? 'active' : ''}`}
      onClick={onClick}
      type="button"
    >
      <span className="nav-icon" aria-hidden="true">{item.icon}</span>
      <span className="nav-label">{item.label}</span>
      {typeof count === 'number' && (
        <span className="nav-count">{count}</span>
      )}
    </button>
  );
}